(function () {
  "use strict";

  const root = document.querySelector('[data-vf-p3-landing]');
  if (!root) return;

  document.body.classList.add('vf-p3-page');
  document.getElementById('header-group')?.setAttribute('hidden', 'hidden');

// ------------------------------
      // Scroll hero progress -> CSS variable
      // ------------------------------

      const hero = root.querySelector('[data-scroll-hero]');
      const heroStage = root.querySelector('[data-hero-stage]');
      const heroContent = root.querySelector('[data-hero-content]');
      const heroVideo = root.querySelector('[data-hero-video]');
      let heroVideoStarted = false;
      let heroVideoCanPlay = true;

      // Cache hero metrics to avoid forcing layout on every scroll tick.
      let heroTopY = 0;
      let heroRunway = 1;
      let heroProgressRaw = 0;
      let heroProgress = 0;
      function computeHeroMetrics() {
        if (!hero) return;
        const rect = hero.getBoundingClientRect();
        heroTopY = window.scrollY + rect.top;
        heroRunway = Math.max(1, hero.offsetHeight - window.innerHeight);

        // Expose runway distance to CSS so the sticky stage can translate by ~the same px
        // the user scrolls through inside the hero.
        if (heroStage) {
          heroStage.style.setProperty('--hero-runway-px', `${heroRunway}px`);
        }
      }

      function clamp(n, a, b) { return Math.max(a, Math.min(b, n)); }

      function updateHeroProgress() {
        if (!hero || !heroStage) return;

        // Use cached metrics (computed on load/resize) to avoid layout thrash.
        const raw = clamp((window.scrollY - heroTopY) / heroRunway, 0, 1);

        // Piecewise remap:
        // - ramp faster early so copy clears sooner
        // - slow down late so full-bleed video "holds" longer
        let p;
        if (raw < 0.22) {
          p = (raw / 0.22) * 0.62;
        } else {
          p = 0.62 + ((raw - 0.22) / 0.78) * 0.38;
        }
        p = clamp(p, 0, 1);
        heroProgressRaw = raw;
        heroProgress = p;

        heroStage.style.setProperty('--hero-progress', String(p));
        heroStage.style.setProperty('--hero-progress-raw', String(raw));

        // Start video on first scroll into the hero runway.
        // On mobile Safari, scrolling counts as a user interaction so play() usually succeeds.
        if (heroVideo && heroVideoCanPlay && !heroVideoStarted && p > 0.01) {
          heroVideoStarted = true;
          const playPromise = heroVideo.play();
          if (playPromise && typeof playPromise.catch === 'function') {
            playPromise.catch(() => {
              // If autoplay is blocked for any reason, leave it paused.
              // (Still looks fine as a poster-like first frame.)
            });
          }
        }

        // If video is mostly out of view, don't let updateHeroProgress immediately restart it.
        if (heroVideo && heroVideoStarted && !heroVideoCanPlay) {
          try { heroVideo.pause(); } catch {}
          heroVideoStarted = false;
        }

        // Toggle "playing" styles (poster fades away) based on actual playback state
        const mediaEl = heroVideo ? heroVideo.closest('.video-hero__media') : null;
        if (mediaEl && heroVideo) {
          const isPlaying = !!(heroVideo.currentTime > 0 && !heroVideo.paused && !heroVideo.ended);
          mediaEl.classList.toggle('is-playing', isPlaying);
        }

        // If user returns to very top, pause again (so entry is not "playing")
        if (heroVideo && heroVideoStarted && p < 0.001) {
          heroVideoStarted = false;
          try { heroVideo.pause(); } catch {}
        }

        // (pause handled via IntersectionObserver for accurate in-view percentage)

        // Show VF + P3 collab mark as soon as the hero copy is effectively gone (not only when the video is nearly gone).
        // Tie to eased progress so it lines up with the actual content fade curve.
        // (Opacity is: 1 - p*1.48) → "gone" around p≈0.67.
        document.body.classList.toggle('has-collab-header', p >= 0.67);

        if (heroContent) {
          // Dim/disable clicks sooner so the transition to full-bleed video happens earlier.
          heroContent.classList.toggle('is-dim', p > 0.45);
        }
      }

      // ------------------------------
      // Live data animation (scroll RPM)
      // ------------------------------

      const liveSection = root.querySelector('[data-live-data]');
      const rpmFill = root.querySelector('[data-rpm-fill]');
      const rpmReadout = root.querySelector('[data-rpm-readout]');

      const els = {
        boost: root.querySelector('[data-param="boost"]'),
        iat: root.querySelector('[data-param="iat"]'),
        oil: root.querySelector('[data-param="oil"]'),
        timing: root.querySelector('[data-param="timing"]'),
        afr: root.querySelector('[data-param="afr"]'),
      };

      // Cache child nodes so we don't query DOM every tick (perf)
      const elCache = {};
      for (const key of Object.keys(els)) {
        const el = els[key];
        if (!el) continue;
        elCache[key] = {
          el,
          textNode: el.firstChild, // text node before unit span
          unitSpan: el.querySelector('.data-unit'),
          lastClass: '',
          lastText: '',
        };
      }

      const params = {
        boost:  { min:  0, max: 30, decimals: 1, unit: 'psi' },
        iat:    { min: 70, max: 185, decimals: 0, unit: '°F' },
        oil:    { min: 170, max: 270, decimals: 0, unit: '°F' },
        timing: { min: -6, max: 2, decimals: 1, unit: '°' },
        afr:    { min: 10.5, max: 14.7, decimals: 1, unit: ':1' },
      };

      const state = {
        active: false,
        lastY: window.scrollY,
        lastTs: performance.now(),
        scrollVel: 0, // px/ms (smoothed)

        // desktop-only: mouse velocity can also "spool" the feed
        mouseVel: 0, // px/ms (smoothed)
        lastMx: null,
        lastMy: null,
        lastMouseTs: 0,

        rpm: 0,
        redline: 8000,

        // idle behavior
        idleRpm: 850,
        idleTarget: 850,
        nextIdleBumpAt: 0,

        // UI throttling
        nextRpmReadoutAt: 0,
        rpmReadoutValue: 0,
        nextDataTickAt: 0,

        // (thermal sims removed for performance)

        values: {
          boost:  { v: 9.2, t: 12.8 },
          iat:    { v: 104, t: 118 },
          oil:    { v: 212, t: 238 },
          timing: { v: -0.8, t: -1.5 },
          afr:    { v: 12.3, t: 11.8 },
        }
      };

      function lerp(a, b, t) { return a + (b - a) * t; }
      function rand(min, max) { return min + Math.random() * (max - min); }

      function formatValue(key, v) {
        const cfg = params[key];
        const fixed = v.toFixed(cfg.decimals);
        return fixed;
      }

      function setValueText(key, v) {
        const cached = elCache[key];
        if (!cached) return;
        const { el, textNode, unitSpan } = cached;
        const cfg = params[key];

        const nextText = formatValue(key, v);
        if (textNode && cached.lastText !== nextText) {
          textNode.nodeValue = nextText;
          cached.lastText = nextText;
        }
        if (unitSpan) {
          // set once; safe to leave, but keep correct if changed
          const nextUnit = cfg.unit.replace('°', '&deg;');
          if (unitSpan.innerHTML !== nextUnit) unitSpan.innerHTML = nextUnit;
        }

        // Dynamic coloring (avoid churn: only change classes when needed)
        let klass = '';
        if (key === 'boost') {
          const psi = Number(v);
          if (psi <= 0.05) klass = 'data-value--teal';
          else if (psi >= 15) klass = 'data-value--danger';
          else if (psi >= 10) klass = 'data-value--warn';
        } else if (key === 'iat') {
          const t = Number(v);
          if (t >= 180) klass = 'data-value--danger';
          else if (t >= 165) klass = 'data-value--warn';
        }

        if (cached.lastClass !== klass) {
          el.classList.remove('data-value--teal', 'data-value--warn', 'data-value--danger');
          if (klass) el.classList.add(klass);
          cached.lastClass = klass;
        }
      }

      function updateScrollVelocity(now) {
        const y = window.scrollY;
        const dy = y - state.lastY;
        const dt = Math.max(16, now - state.lastTs);

        const v = Math.abs(dy) / dt; // px/ms

        // smooth a bit; keeps it stable
        state.scrollVel = lerp(state.scrollVel, v, 0.25);

        state.lastY = y;
        state.lastTs = now;
      }

      function updateMouseVelocity(e) {
        if (!state.active) return;
        const now = performance.now();

        if (state.lastMx == null || state.lastMy == null) {
          state.lastMx = e.clientX;
          state.lastMy = e.clientY;
          state.lastMouseTs = now;
          return;
        }

        const dx = e.clientX - state.lastMx;
        const dy = e.clientY - state.lastMy;
        const dt = Math.max(16, now - state.lastMouseTs);

        const dist = Math.hypot(dx, dy);
        const v = dist / dt; // px/ms

        // Smooth; also decay naturally when you stop moving
        state.mouseVel = lerp(state.mouseVel, v, 0.22);

        state.lastMx = e.clientX;
        state.lastMy = e.clientY;
        state.lastMouseTs = now;
      }

      function updateRpm(now) {
        // Map scroll velocity to RPM, but keep a realistic idle when not scrolling.
        // Make it slightly more sensitive than before.

        // Idle: realistic random jitter between ~800–900, changing frequently.
        // We update an idle target every ~90–180ms and ease toward it.
        if (!state.nextIdleBumpAt || now >= state.nextIdleBumpAt) {
          state.idleTarget = rand(800, 900);
          state.nextIdleBumpAt = now + rand(90, 180);
        }
        state.idleRpm = lerp(state.idleRpm, state.idleTarget, 0.22);
        const idle = clamp(state.idleRpm, 780, 940);

        const vMax = 1.75; // lower = more sensitive

        // Desktop: mouse movement velocity can also contribute (so it feels alive even when not scrolling)
        // We scale mouseVel down a bit so it doesn't overpower scroll.
        const mouseScale = 0.85;
        const inputVel = Math.max(state.scrollVel, state.mouseVel * mouseScale);

        const v = clamp(inputVel, 0, vMax);
        const vNorm = clamp(v / vMax, 0, 1);

        // boost low-speed sensitivity with an exponent < 1
        const norm = Math.pow(vNorm, 0.70);

        // While scrolling, RPM climbs; while stopped, it falls back to idle.
        // Never go below idle so it feels like a running engine.
        const rpmTarget = Math.max(idle, norm * state.redline);

        // spool up faster than decay (slower drop per request)
        const up = 0.28;
        const down = 0.055; // slower drop
        const k = rpmTarget > state.rpm ? up : down;
        state.rpm = lerp(state.rpm, rpmTarget, k);

        const rpmNorm = clamp(state.rpm / state.redline, 0, 1);

        if (rpmFill) {
          rpmFill.style.width = (rpmNorm * 100).toFixed(1) + '%';

          // Color ramp: green -> yellow -> orange -> red as we approach redline.
          // Hue 120 (green) down to ~0 (red).
          const hue = (1 - rpmNorm) * 120;
          rpmFill.style.background = `linear-gradient(90deg, hsla(${hue}, 90%, 55%, 0.85), hsla(${Math.max(hue - 12, 0)}, 95%, 50%, 1))`;
          rpmFill.style.boxShadow = `0 0 ${10 + rpmNorm * 22}px hsla(${Math.max(hue - 10, 0)}, 95%, 55%, ${0.25 + rpmNorm * 0.38})`;
        }
        if (rpmReadout) {
          // Keep the bar super responsive, but update the numeric readout slightly slower
          // so it feels less twitchy.
          const intervalMs = 90; // ~11Hz
          if (!state.nextRpmReadoutAt || now >= state.nextRpmReadoutAt) {
            state.rpmReadoutValue = Math.round(state.rpm);
            state.nextRpmReadoutAt = now + intervalMs;
          }
          rpmReadout.textContent = state.rpmReadoutValue + ' RPM';
        }
      }

      function driftTargets(mult, now) {
        // Lightweight drift (keeps page snappy). No per-frame thermal simulation.
        const s = (0.08 + mult * 0.55);

        // BOOST follows RPM naturally:
        // - idle (~800-900 rpm): ~0.0 psi
        // - ramps to ~16psi by ~4000 rpm
        // - hovers ~16-17psi near redline
        const rpm = state.rpm;
        let boostTarget = 0;
        if (rpm < 1100) {
          boostTarget = 0;
        } else if (rpm < 4000) {
          const t = (rpm - 1100) / (4000 - 1100);
          boostTarget = 16 * clamp(t, 0, 1);
        } else {
          const t = (rpm - 4000) / (state.redline - 4000);
          boostTarget = 16 + clamp(t, 0, 1) * 1.0;
        }
        const breathe = (Math.sin(now / 170) + Math.sin(now / 73)) * 0.10 * clamp(mult * 1.2, 0, 1);
        boostTarget = clamp(boostTarget + breathe, 0, 17.2);
        state.values.boost.t = boostTarget;

        // Temps: gentle drift (as before), still bounded.
        state.values.iat.t = clamp(state.values.iat.t + rand(-0.30, 0.45) * s, params.iat.min, 185);
        state.values.oil.t = clamp(state.values.oil.t + rand(-0.22, 0.30) * s, params.oil.min, params.oil.max);

        // Timing correction: slight wobble, tends more negative under load.
        const loadBias = clamp(state.values.boost.t / 17.2, 0, 1) * 0.9;
        state.values.timing.t = clamp(state.values.timing.t + (rand(-0.12, 0.12) - loadBias * 0.08) * s, params.timing.min, params.timing.max);

        // AFR (lower under boost)
        const afrBias = clamp(state.values.boost.t / 17.2, 0, 1) * 0.22;
        state.values.afr.t = clamp(state.values.afr.t + (rand(-0.08, 0.08) - afrBias) * s, params.afr.min, params.afr.max);
      }

      function animateValues(mult) {
        // When scrolling, we update/lerp faster.
        const baseLerp = 0.06;
        const lerpBoost = 0.14 * mult; // 0..0.14
        const k = clamp(baseLerp + lerpBoost, 0.06, 0.22);

        for (const key of Object.keys(state.values)) {
          const item = state.values[key];
          item.v = lerp(item.v, item.t, k);
          setValueText(key, item.v);
        }
      }

      let raf = 0;
      function loop(now) {
        if (!state.active) return;

        updateHeroProgress();
        updateScrollVelocity(now);

        // If mouse isn't moving, gently decay mouseVel so it doesn't "stick".
        if (supportsMouse) {
          state.mouseVel = lerp(state.mouseVel, 0, 0.06);
        }

        updateRpm(now);
        const rpmNorm = clamp(state.rpm / state.redline, 0, 1);

        // PERF: keep hero/RPM bar smooth, but tick value updates at a modest rate.
        // When the hero video is paused (mostly off-screen), we can safely update faster.
        const heroPaused = !!(heroVideo && heroVideo.paused);
        const intervalMs = heroPaused
          ? clamp(110 - rpmNorm * 60, 35, 110)   // ~9–28Hz (snappier) when video is paused
          : clamp(180 - rpmNorm * 90, 70, 180);  // ~5.5–14Hz when video is active

        if (!state.nextDataTickAt || now >= state.nextDataTickAt) {
          state.nextDataTickAt = now + intervalMs;
          driftTargets(rpmNorm, now);
          animateValues(rpmNorm);
        }

        raf = requestAnimationFrame(loop);
      }

      // Desktop pointer detection (avoid touch devices)
      const supportsMouse = (() => {
        try {
          return window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        } catch {
          return false;
        }
      })();

      // Attach mouse listener only while the live-data loop is active (reduces event overhead)
      function attachMouse() {
        if (!supportsMouse) return;
        window.addEventListener('mousemove', updateMouseVelocity, { passive: true });
      }
      function detachMouse() {
        if (!supportsMouse) return;
        window.removeEventListener('mousemove', updateMouseVelocity);
      }

      // Pause hero video once >80% out of view (intersectionRatio < 0.2)
      // and resume when it comes back into view.
      if (heroStage && heroVideo && 'IntersectionObserver' in window) {
        const heroIO = new IntersectionObserver((entries) => {
          const entry = entries[0];
          if (!entry) return;

          const visibleRatio = entry.intersectionRatio || 0;
          // Pause once >60% is off-screen (visibleRatio < 0.4)
          heroVideoCanPlay = visibleRatio >= 0.4;

          // If mostly off-screen, pause to save CPU/GPU.
          if (!heroVideoCanPlay) {
            try { heroVideo.pause(); } catch {}
            heroVideoStarted = false;
          }
          // Resuming is handled by updateHeroProgress (gated by heroVideoCanPlay + scroll).
        }, { threshold: [0, 0.2, 0.35, 0.5, 1] });

        heroIO.observe(heroStage);
      }

      // Only run the loop when live data section is near viewport
      if (liveSection && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          const entry = entries[0];
          // Run live data when section is near viewport.
          // (Don't hard-gate on heroDone; on mobile the hero runway is longer, which prevented updates.)
          const shouldRun = !!(entry && entry.isIntersecting);

          if (shouldRun && !state.active) {
            state.active = true;
            state.lastY = window.scrollY;
            state.lastTs = performance.now();
            state.nextDataTickAt = 0; // reset tick scheduler
            attachMouse();
            raf = requestAnimationFrame(loop);
          } else if (!shouldRun && state.active) {
            state.active = false;
            // decay mouse velocity while paused
            state.mouseVel = 0;
            detachMouse();
            if (raf) cancelAnimationFrame(raf);
          }
        }, { root: null, rootMargin: '220px 0px 220px 0px', threshold: 0.01 });

        io.observe(liveSection);
      } else {
        // fallback: always run
        state.active = true;
        raf = requestAnimationFrame(loop);
      }

      // Ensure hero progress updates even before live section is reached
      window.addEventListener('scroll', () => {
        // lightweight: just update the hero CSS var; live loop handles velocity + values
        updateHeroProgress();
      }, { passive: true });

      window.addEventListener('resize', () => {
        computeHeroMetrics();
        updateHeroProgress();
      });

      // ------------------------------
      // Make → Model selector (driven by p3-makes-models.json)
      // ------------------------------

      const makeApp = root.querySelector('[data-make-selector]');
      const makeGrid = root.querySelector('[data-make-grid]');
      const modelPanel = root.querySelector('[data-model-panel]');
      const modelGrid = root.querySelector('[data-model-grid]');
      const modelTitle = root.querySelector('[data-model-title]');
      const modelBack = root.querySelector('[data-model-back]');

      function getMakeFromUrl() {
        const u = new URL(window.location.href);
        return (u.searchParams.get('make') || '').trim().toLowerCase();
      }

      function setMakeInUrl(makeSlug) {
        const u = new URL(window.location.href);
        if (makeSlug) {
          u.searchParams.set('make', makeSlug);
          // keep the user anchored in this section when sharing deep links
          u.hash = 'make-selector';
        } else {
          u.searchParams.delete('make');
        }
        history.pushState({ make: makeSlug || null }, '', u.toString());
      }

      function renderMakes(makes) {
        if (!makeGrid) return;
        makeGrid.innerHTML = '';
        for (const m of makes) {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'make-selector__item';
          btn.setAttribute('role', 'listitem');
          btn.dataset.makeSlug = m.makeSlug;

          btn.innerHTML = `
            <div class="make-selector__logo"><img src="${(window.VF_P3_LOGOS && window.VF_P3_LOGOS[m.makeSlug]) || m.logoSrc}" alt="${m.makeName} logo"></div>
            <div class="make-selector__name">${m.makeName}</div>
          `;

          btn.addEventListener('click', () => {
            showModels(m.makeSlug);
          });

          makeGrid.appendChild(btn);
        }
      }

      let makeData = null;

      function findMake(makeSlug) {
        if (!makeData) return null;
        return (makeData.makes || []).find((m) => (m.makeSlug || '').toLowerCase() === makeSlug);
      }

      function showModels(makeSlug) {
        const m = findMake(makeSlug);
        if (!m) return;

        if (modelTitle) modelTitle.textContent = `${m.makeName} Models`;
        if (modelGrid) {
          modelGrid.innerHTML = '';
          for (const model of (m.models || [])) {
            const a = document.createElement('a');
            a.className = 'model-selector__item';
            // Use absolute productUrl when available so dev server doesn't try to serve /products/*.
            // Same tab (Asif, 2026-10-01): these are our own product pages.
            a.href = model.productUrl || model.productPath;
            a.innerHTML = `
              <div class="model-selector__item-label">${model.label}</div>
              <div class="model-selector__item-meta">View product details</div>
            `;
            modelGrid.appendChild(a);
          }
        }

        makeApp?.classList.add('is-showing-models');
        setMakeInUrl(makeSlug);

        // On mobile, if the user clicked a make near the bottom of the list,
        // the models panel can render "above" the current scroll position.
        // Snap them to the top of the selector so it never looks empty.
        const modelHeader = root.querySelector('[data-model-header]');
        if (modelHeader) {
          // Account for the fixed header so the back arrow + title aren't hidden.
          const fixedHeader = root.querySelector('.header');
          const headerH = fixedHeader ? fixedHeader.getBoundingClientRect().height : 0;
          const y = modelHeader.getBoundingClientRect().top + window.scrollY - headerH - 12;
          window.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
        }
      }

      function showMakes() {
        makeApp?.classList.remove('is-showing-models');
        setMakeInUrl('');
      }

      modelBack?.addEventListener('click', showMakes);

      async function loadMakeModels() {
        if (!makeApp || !makeGrid) return;

        // Vite dev server: /@fs lets us load the generated JSON directly from scripts/.
        const urls = [root.dataset.makesUrl].filter(Boolean);

        for (const url of urls) {
          try {
            const res = await fetch(url, { cache: 'no-store' });
            if (!res.ok) continue;
            makeData = await res.json();
            break;
          } catch {
            // try next
          }
        }

        if (!makeData || !Array.isArray(makeData.makes)) {
          makeGrid.innerHTML = '<p style="color: rgba(255,255,255,0.65);">Make selector data not available.</p>';
          return;
        }

        renderMakes(makeData.makes);

        // Deep-link support: /pages/p3-performance-gauges?make=vw
        const initialMake = getMakeFromUrl();
        if (initialMake) {
          // wait a tick so layout is ready before animating
          requestAnimationFrame(() => showModels(initialMake));
        }
      }

      window.addEventListener('popstate', () => {
        const m = getMakeFromUrl();
        if (m) showModels(m);
        else showMakes();
      });

      // Initial metrics
      computeHeroMetrics();

      // Initialize texts so it's not zeroed on load
      for (const key of Object.keys(state.values)) {
        setValueText(key, state.values[key].v);
      }

      updateHeroProgress();
      loadMakeModels();
})();
